import { ChangeDetectionStrategy, Component, computed, effect, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { Error } from '../../../../../public-user-component/auth/shared/error/error';
import { CloseButtonComponent } from '../../../../../../shared/ui/close-button/close-button.component';

@Component({
  selector: 'app-collect-cash-popup',
  standalone: true,
  imports: [FormsModule, DecimalPipe, Error, CloseButtonComponent],
  templateUrl: './collect-cash-popup.html',
  styleUrl: './collect-cash-popup.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectCashPopup {
  expectedAmount = input.required<number>();
  loading = input<boolean>(false);
  err = input<string>();

  actualAmount = model<number | null>(null);
  comment = model<string>('');

  submit = output<{ actualAmount: number; comment: string }>();
  closePopup = output();

  readonly maxCommentLength = 4000;

  amountTouched = signal(false);

  showMismatchWarning = signal(false);

  discrepancy = computed(() => {
    const actual = this.actualAmount();
    if (actual === null || actual === undefined) return null;
    return Math.round((actual - this.expectedAmount()) * 100) / 100;
  });

  discrepancyState = computed<'zero' | 'negative' | 'positive' | null>(() => {
    const diff = this.discrepancy();
    if (diff === null) return null;
    if (diff === 0) return 'zero';
    return diff < 0 ? 'negative' : 'positive';
  });

  discrepancyLabel = computed(() => {
    const diff = this.discrepancy();
    const state = this.discrepancyState();
    if (diff === null || state === null) return '';
    const abs = Math.abs(diff).toFixed(2);
    switch (state) {
      case 'zero':
        return $localize`:@@noDiscrepancy:No discrepancy`;
      case 'negative':
        return $localize`:@@shortByAmount:Short by ${abs}:INTERPOLATION: EGP`;
      case 'positive':
        return $localize`:@@surplusOfAmount:Surplus of ${abs}:INTERPOLATION: EGP`;
    }
  });

  amountError = computed<string | null>(() => {
    const actual = this.actualAmount();

    if (actual === null || (actual as unknown as string) === '') {
      return $localize`:@@actualAmountRequired:Actual Amount is required.`;
    }

    if (Number.isNaN(actual)) {
      return $localize`:@@invalidAmount:Please enter a valid amount.`;
    }

    if (actual < 0) {
      return $localize`:@@amountNegative:Amount cannot be negative.`;
    }

    if (!/^\d+(\.\d{1,2})?$/.test(String(actual))) {
      return $localize`:@@invalidAmount2:Please enter a valid amount.`;
    }

    if (this.discrepancy() !== 0) {
      return $localize`:@@amountMismatch:The entered amount does not match the expected amount. Please review.`;
    }

    return null;
  });
  commentError = computed<string | null>(() => {
    if (this.comment().length > this.maxCommentLength) {
      return $localize`:@@commentTooLong:Comment cannot exceed 4000 characters.`;
    }
    return null;
  });

  canConfirm = computed(() => {
    return !this.amountError() && !this.commentError() && this.discrepancyState() === 'zero' && !this.loading();
  });

  onAmountBlur() {
    this.amountTouched.set(true);
  }

  onSubmit(e: Event) {
    e.preventDefault();

    if (!this.canConfirm()) return;

    this.submit.emit({
      actualAmount: this.actualAmount() as number,
      comment: this.comment(),
    });
  }

  onClosePopup() {
    this.closePopup.emit();
  }
}
